import { takeLatest, all, select, call, put } from 'redux-saga/effects';

import { requestAndSet, heliumReceive } from 'utils/helpers';
import { opTime } from 'utils/dates';

import { visitorSearchActions as actions } from '../slices/visitorSearchSlice';
import { DATE_TIME_FORMAT } from '../../constants/dates';
import { Location } from '../../types/locationTypes';
import { VisitorSearch } from '../../types/visitorSearchTypes';

interface ImmutableStateReader {
  getIn: (path: string[]) => unknown;
}

interface SearchVisitorsResponse {
  data?: VisitorSearch[];
}

export function* searchVisitorsSaga(
  action: ReturnType<typeof actions.searchVisitors>,
): Generator {
  const { orgId, searchDateRange, ...params } = action.payload;

  // Store searchDateRange for client-side filtering in the table
  yield put(actions.setSearchDateRange(searchDateRange));

  yield requestAndSet('getSearchVisitors', [orgId], {
    queryStringParams: params,
    createSetterAction: actions.setVisitors,
    loadingAction: (v: boolean) =>
      actions.setLoading({ key: 'searchVisitorsLoading', value: v }),
  });

  yield put(actions.setLastSearchParams(params));
}

export function* searchVisitorsTodaySaga(
  action: ReturnType<typeof actions.searchVisitorsToday>,
): Generator {
  const { orgId } = action.payload;

  const globalLocationId = (yield select((state: ImmutableStateReader) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  )) as number;

  if (globalLocationId === -1) {
    const locations = (yield select((state: ImmutableStateReader) => {
      const rawLocations = state.getIn(['locations', 'locations']) as
        | { toJS?: () => Location[] }
        | Location[]
        | undefined;

      if (
        rawLocations &&
        typeof rawLocations === 'object' &&
        'toJS' in rawLocations &&
        typeof rawLocations.toJS === 'function'
      ) {
        return rawLocations.toJS();
      }

      return (rawLocations as Location[]) || [];
    })) as Location[];

    try {
      yield put(
        actions.setLoading({ key: 'searchVisitorsTodayLoading', value: true }),
      );

      const allVisitors: VisitorSearch[] = [];

      const userTz = opTime.tz.guess();

      for (const location of locations) {
        let startDate = opTime()
          .tz(userTz)
          .startOf('day')
          .format(DATE_TIME_FORMAT);
        let endDate = opTime().tz(userTz).endOf('day').format(DATE_TIME_FORMAT);

        // Expand to cover all possible timezones
        startDate = opTime(startDate)
          .subtract(14, 'hours')
          .format(DATE_TIME_FORMAT);
        endDate = opTime(endDate).add(12, 'hours').format(DATE_TIME_FORMAT);

        const response = (yield call(
          heliumReceive,
          'getSearchVisitors',
          [orgId],
          {
            queryStringParams: {
              locationId: location.id,
              startDate,
              endDate,
            },
          },
        )) as SearchVisitorsResponse;

        if (response?.data) {
          // Add locationId to each visitor for filtering
          const visitorsWithLocation = response.data.map((visitor) => ({
            ...visitor,
            locationId: location.id,
          }));
          allVisitors.push(...visitorsWithLocation);
        }
      }

      yield put(
        actions.setVisitorsToday({
          data: allVisitors,
          meta: null,
          totalCount: allVisitors.length,
          filteredCount: allVisitors.length,
        }),
      );
    } finally {
      yield put(
        actions.setLoading({ key: 'searchVisitorsTodayLoading', value: false }),
      );
    }
  } else {
    const globalLocation = (yield select((state: ImmutableStateReader) => {
      const rawLocation = state.getIn(['locations', 'globalLocation']) as
        | { toJS?: () => Location | null }
        | Location
        | null
        | undefined;

      if (
        rawLocation &&
        typeof rawLocation === 'object' &&
        'toJS' in rawLocation &&
        typeof rawLocation.toJS === 'function'
      ) {
        return rawLocation.toJS();
      }

      return (rawLocation as Location | null) || null;
    })) as Location | null;

    const tz = globalLocation?.timezone || 'UTC';
    const startDate = opTime()
      .tz(tz)
      .startOf('day')
      .utc()
      .format(DATE_TIME_FORMAT);
    const endDate = opTime().tz(tz).endOf('day').utc().format(DATE_TIME_FORMAT);

    yield requestAndSet('getSearchVisitors', [orgId], {
      queryStringParams: {
        locationId: globalLocationId,
        startDate,
        endDate,
      },
      createSetterAction: actions.setVisitorsToday,
      loadingAction: (v: boolean) =>
        actions.setLoading({ key: 'searchVisitorsTodayLoading', value: v }),
    });
  }
}

export default function* visitorSearchSaga(): Generator {
  yield all([
    takeLatest(actions.searchVisitors.type, searchVisitorsSaga),
    takeLatest(actions.searchVisitorsToday.type, searchVisitorsTodaySaga),
  ]);
}
